import React from 'react';
import { get, noop  } from 'lodash';
import ConfirmModal from 'src/components/confirm-modal';
import PeopleItem from 'src/components/people-item';
import {useTranslation} from 'react-i18next'
import FormItem from 'src/components/form-item';
import './style.scss'

/**
 * Renders a confirm delete deallocation modal component.
 * 
 * @param {object} props - The component props.
 * @param {object} props.people - The deallocation object.
 * @param {boolean} props.loading - Indicates if the confirm button is in a loading state.
 * @param {boolean} props.isOwner - Indicates if the user is the owner of the deallocation.
 * @param {function} props.onConfirm - The function to confirm the deletion.
 * @param {function} props.onCancel - The function to cancel the deletion.
 * @returns {JSX.Element|null} The rendered confirm delete modal component.
 * @example
 *  <ConfirmDeleteModal
        loading={deleteFetching}
        people={deletingDeallocation}
        onConfirm={deleteDeallocation}
        isOwner={isOwnerOfDeleting}
        onCancel={onCancelDelete}
    />
 */
function DeleteModal({
    people,
    loading,
    isOwner,
    onConfirm,
    onCancel
}) {
    const { t } = useTranslation()
    
    if (!people) return null;

    const needs = people.needs;
    const responsible = get(people, 'responsible_deallocation.login');

    return (
        <ConfirmModal
            confirmLoading={loading}
            visible={people}
            title={t('confirmRemoval')}
            onConfirm={onConfirm}
            onCancel={onCancel}
            okText={t('delete')}
            width={600}
            cancelText={t('cancel')}>
            <div id="confirm-delete-people-modal" data-testid="delete-confirm-modal">
                {t('deleteFollowingPeople')}

                <div className="people-info" data-testid="people-info-container">
                    <FormItem label={t('profile')}>
                        <PeopleItem people={people} />
                    </FormItem>
                </div>

                { needs.length > 0 && (
                    <div data-testid="fit-presence-alert">
                        <p>{t('currentlyMatched', { needs })}</p>
                        <p>{t('needWillContinue')}</p>
                    </div>
                ) }

                { !isOwner && (
                    <p data-testid="notification-alert">{t('responsiblePeople', {responsible})}</p>
                ) }
            </div>
        </ConfirmModal>
    );
}

DeleteModal.defaultProps = {
    people: {},
    loading: false,
    isOwner: false,
    onConfirm: noop,
    onCancel: noop
};

export default DeleteModal;
